import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "According to research, strong social support can reduce depression risk by what percentage range?",
    options: [
      "10-20%",
      "20-30%",
      "40-60%",
      "70-80%"
    ],
    correctAnswer: 2,
    explanation: "Research shows that strong social support networks can reduce the risk of depression by 40-60%, demonstrating their significant protective effects on mental health."
  },
  {
    question: "What is the 'Helper's High'?",
    options: [
      "A feeling of superiority when helping others",
      "The release of endorphins when engaging in prosocial behavior",
      "A type of exercise high from volunteering",
      "The satisfaction of completing community service"
    ],
    correctAnswer: 1,
    explanation: "The 'Helper's High' refers to the scientific evidence that helping others releases endorphins, creating positive social feedback loops and enhancing both individual and collective well-being."
  },
  {
    question: "Which of the following is NOT a benefit of community connectedness?",
    options: [
      "Feeling part of something larger than yourself",
      "Shared identity and common purpose",
      "Guaranteed financial success",
      "Collective efficacy and mutual support"
    ],
    correctAnswer: 2,
    explanation: "While community connectedness provides many benefits including shared identity, common purpose, and collective efficacy, it does not guarantee financial success. The other options are all established benefits."
  },
  {
    question: "What is the recommended structure for a balanced support ecosystem?",
    options: [
      "Only close relationships, no casual connections",
      "Inner circle (2-5 close), middle circle (10-15 moderate), outer circle (50+ loose)",
      "Only online connections for convenience",
      "As many connections as possible, regardless of depth"
    ],
    correctAnswer: 1,
    explanation: "A balanced support ecosystem includes an inner circle of 2-5 close relationships, a middle circle of 10-15 moderate relationships, and an outer circle of 50+ loose connections for different types of support."
  },
  {
    question: "How does socio-relational well-being integrate with physical health?",
    options: [
      "It has no effect on physical health",
      "Accountability partners improve exercise adherence and group activities provide motivation",
      "It only affects mental health, not physical",
      "It replaces the need for physical activity"
    ],
    correctAnswer: 1,
    explanation: "Socio-relational well-being integrates with physical health through accountability partners for exercise, group fitness activities for motivation, social dining for healthy eating, and walking groups that combine physical activity with social connection."
  }
];

const SocioRelationalModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('socioRelationalM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>Integrating relational well-being across life domains</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Creating sustainable social habits</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Building community connections and support networks</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your socio-relational wellness journey by building long-term community connections!</p>
            <p>Ready to learn about creating sustainable social habits for life?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/socio-relational-resources')}
          >
            Return to Socio-Relational Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left socio-relational-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Integrating Socio-Relational Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/socio-relational-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule5QuizPage;

